class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    // Delegation survives Shopify replacing the drawer's sections.
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Cart-page updates do not include the drawer in their bundled sections.
      if (event.source === 'cart-items' && !event.cartData?.sections?.['cart-drawer']) this.dirty = true;
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');
    const openCart = async (event) => {
      event.preventDefault();
      this.open(cartLink);
      if (this.dirty) {
        this.setBusy(true);
        try { await this.refresh(); } catch { this.showRefreshError(); }
        finally { this.setBusy(false); }
      }
    };
    cartLink.addEventListener('click', openCart);
    cartLink.addEventListener('keydown', (event) => {
      if (event.code === 'Space') openCart(event);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.classList.contains('active')) return;
    this.inert = false;
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    // Do not rely on transitionend: reduced-motion users have no transition.
    requestAnimationFrame(() => this.focusContents());
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  focusContents(element) {
    if (!this.classList.contains('active')) return;
    const inner = this.querySelector('.drawer__inner');
    const note = inner.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    const container = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : inner;
    container.setAttribute('tabindex', '-1');
    trapFocus(container, element || container);
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble'));
    this.inert = true;
    document.body.classList.remove('overflow-hidden');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
  }

  setBusy(busy) {
    this.busy = busy;
    this.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('[name="checkout"]').forEach((button) => {
      button.disabled = busy || this.classList.contains('is-empty');
    });
  }

  setSummaryAccessibility(note) {
    note.setAttribute('role', 'button');
    note.setAttribute('aria-expanded', String(note.parentElement.open));
    if (note.nextElementSibling.id) note.setAttribute('aria-controls', note.nextElementSibling.id);
    note.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', String(!event.currentTarget.closest('details').open));
    });
    note.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  applySections(sections) {
    const fragments = this.getSectionsToRender().map((section) => {
      const html = sections?.[section.id];
      const source = html && new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
      if (!source) throw new Error('Missing cart section');
      return { section, source };
    });
    // Validate every fragment before replacing any markup.
    for (const { section, source } of fragments) {
      const target = document.querySelector(section.selector || `#${section.id}`);
      if (target) target.innerHTML = source.innerHTML;
    }
    this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
    this.dirty = false;
    this.setBusy(!!this.busy);
    this.focusContents();
  }

  async refresh() {
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('sections', this.getSectionsToRender().map((section) => section.id).join(','));
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not refresh bag');
    this.applySections(await response.json());
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    try {
      this.applySections(parsedState.sections);
    } catch {
      // A successful add can have null section HTML. Re-read the cart; never repeat the add.
      try { await this.refresh(); } catch { this.showRefreshError(); }
    }
    this.open();
    const feedback = this.querySelector('#ih-cart-feedback');
    if (feedback && !this.dirty) feedback.textContent = 'Added to your bag.';
  }

  showRefreshError() {
    this.dirty = true;
    const error = this.querySelector('#CartDrawer-CartErrors');
    if (error) error.textContent = 'Your bag changed, but could not be refreshed. Open View cart to see the latest items.';
    // The cart page remains a reliable recovery path even if the old drawer was empty.
    const fallback = this.querySelector('.cart-drawer__empty-content > a');
    if (fallback) {
      this.querySelector('.cart__empty-text').textContent = 'See your updated bag.';
      this.querySelector('.cart-drawer__empty-content .ih-eyebrow').textContent = 'We couldn’t refresh the items here.';
      fallback.href = routes.cart_url;
      fallback.firstChild.textContent = 'View cart ';
    }
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  setActiveElement(element) {
    // An add-on button is replaced after adding; keep the original page opener.
    if (element && !this.contains(element)) this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate(event) {
    // ProductForm renders its bundled sections. A second fetch would race that render.
    if (event?.source === 'product-form') return;
    const drawer = this.closest('cart-drawer');
    return drawer.refresh().catch(() => drawer.showRefreshError());
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
